import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { startReport, UserKeys } from '../../api/users-api';
import { PermissionItem, ReportModel, ReportType } from '../../../types';
import { useStore } from '../../components/state/zustand-store';
import { handleErrors } from '../../utils/errors';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../utils/roles/client-roles';
import { pagesConfig } from '../pages-config';
import { useAnalytics } from '../analytics/create-events';
import * as analytics from '../analytics/analytics.json';
import { ModuleList } from '../analytics/module-list';

interface UseReportActionsOptions {
  toolsRoles: string[];
  changeLogRoles: string[];
  removedHiddenReports: ReportType[];
  /**
   * Optional. Called in the run mutation's onSettled after the built-in
   * analytics event fires. Use for additional side-effects specific to the
   * calling component.
   */
  onRunSettled?: (report: ReportModel | undefined) => void;
  changeLogPermission?: PermissionItem;
}

export function useReportActions({
  toolsRoles,
  changeLogRoles,
  removedHiddenReports,
  onRunSettled,
  changeLogPermission,
}: UseReportActionsOptions) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();
  const { createEvent } = useAnalytics();

  //  Run report
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [reportForRun, setReportForRun] = useState<ReportModel | null>(null);

  const [openRunConfirmDialog, setOpenRunConfirmDialog] = useState(false);

  const { mutate: runReport, isPending: isRunning } = useMutation({
    mutationFn: (r: ReportModel) => startReport(r, changeLogMessage),
    onMutate: (r: ReportModel) => {
      const previous = queryClient.getQueryData<ReportModel[]>([
        UserKeys.Reports,
      ]);
      queryClient.setQueryData<ReportModel[]>(
        [UserKeys.Reports],
        (old) =>
          old?.map((report) =>
            report.id === r.id
              ? { ...report, status: 'RUNNING' as const }
              : report
          )
      );
      return { previous };
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The generated report has opened in a new tab.',
      });
      window.open(
        `https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      queryClient.setQueryData<ReportModel[] | undefined>(
        [UserKeys.Reports],
        (oldData) => {
          if (!oldData) return;
          return [
            ...oldData.filter((r) => r.id !== data.id),
            { ...data, status: 'RUNNING' as const },
          ];
        }
      );
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData([UserKeys.Reports], context.previous);
      }
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue generating the report. Please refresh and try again.',
      });
    },
    onSettled: (_data, _error, variables) => {
      setOpenChangeLogDialog(false);
      setOpenRunConfirmDialog(false);
      setChangeLogMessage('');
      createEvent(analytics.report_center.reports.run_report, {
        module: ModuleList.modules.reportCenter.name,
        section: ModuleList.modules.reportCenter.sections.reports,
        reportType: variables?.reportType,
      });
      onRunSettled?.(variables);
    },
  });

  function handleStartReport(r: ReportModel) {
    setReportForRun(r);
    setOpenRunConfirmDialog(true);
  }

  function handleConfirmRunDialog() {
    if (!reportForRun) return;
    const r = reportForRun;
    const reportType = removedHiddenReports.find(
      (t) => t.name === r.reportType
    );
    const matchingRole = reportType
      ? Object.keys(ToolsRoles).find(
          (k) => ToolsRoles[k].value === reportType.role
        )
      : null;

    const needsChangeLogInRunReport = isAbleToSeeUIElement(
      changeLogRoles,
      changeLogPermission?.value || ''
    );

    const needsChangelog =
      (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.MaunalRunReport.value) &&
        isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value)) ||
      (matchingRole != null &&
        isAbleToSeeUIElement(changeLogRoles, ToolsRoles[matchingRole].value) &&
        isAbleToSeeUIElement(toolsRoles, ToolsRoles[matchingRole].value));

    if (needsChangelog || needsChangeLogInRunReport) {
      setOpenRunConfirmDialog(false);
      setOpenChangeLogDialog(true);
    } else {
      setChangeLogMessage('');
      runReport(r);
    }
  }

  function handleConfirmRun() {
    if (reportForRun) runReport(reportForRun);
  }

  // Edit report

  function handleEditReport(reportId: number) {
    navigate(
      `/${pagesConfig.reports.link}/${pagesConfig.editReport.link.replace(
        ':id',
        reportId.toString()
      )}`
    );
  }

  // Delete report (dialog open state only — what to delete is caller-specific)

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  return {
    // Run
    handleStartReport,
    handleConfirmRunDialog,
    handleConfirmRun,
    isRunning,
    reportForRun,
    openRunConfirmDialog,
    closeRunConfirmDialog: () => setOpenRunConfirmDialog(false),
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    closeChangeLogDialog: () => {
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    // Edit
    handleEditReport,
    // Delete
    openDeleteDialog,
    setOpenDeleteDialog,
  };
}
